import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Tent, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // verifying, success, expired, error
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setError('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-email?token=${token}`);
      setStatus('success');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Verification failed';
      if (errorMessage.toLowerCase().includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Tent className="w-7 h-7 text-primary-foreground" />
          </div>
          <span className="font-heading font-semibold text-lg">Campia</span>
        </Link>

        <div className="bg-card rounded-2xl border border-border p-8">
          {status === 'verifying' && (
            <div className="space-y-4">
              <Loader2 className="w-16 h-16 text-primary mx-auto animate-spin" />
              <h1 className="font-heading font-bold text-2xl">Verifying Email...</h1>
              <p className="text-muted-foreground">Please wait while we verify your email address.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Email Verified!</h1>
              <p className="text-muted-foreground">
                Your email has been verified successfully. Redirecting to login...
              </p>
            </div>
          )}

          {status === 'expired' && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <RefreshCw className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Link Expired</h1>
              <p className="text-muted-foreground">
                This verification link has expired. Please log in to your account and request a new verification email.
              </p>
              <Link 
                to="/login" 
                className="inline-block"
              >
                <Button className="rounded-full">
                  Go to Login
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <h1 className="font-heading font-bold text-2xl">Verification Failed</h1>
              <p className="text-muted-foreground">{error}</p>
              <Link 
                to="/login" 
                className="inline-block text-primary font-medium hover:underline"
              >
                Go to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
